import { Collapse, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";
import {
  useWorkplaceComments,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import pluralize from "pluralize";
import { type MouseEvent, useState } from "react";

import { logEvent } from "../../../../lib/analytics";
import { isViewerAuthor } from "../utils";
import { INITIAL_REPLIES_TO_SHOW } from "./constants";
import { InlineReplyCard } from "./InlineReplyCard";
import { useUpdateCachedComment } from "./useUpdateCachedComment";

interface InlineRepliesSectionProps {
  parentComment: WorkplaceCommentType;
  workplaceId: string;
  viewerUserId: string;
}

export function InlineRepliesSection(props: InlineRepliesSectionProps) {
  const { parentComment, workplaceId, viewerUserId } = props;
  const { totalReplies } = parentComment.attributes.aggregates;

  const [isExpanded, setIsExpanded] = useState(false);

  const commentsParams = {
    workplaceId,
    filter: { parentCommentId: parentComment.id },
  };

  const { data: replies } = useWorkplaceComments(commentsParams, {
    enabled: totalReplies > 0,
  });

  const { updateComment, deleteComment } = useUpdateCachedComment({
    workplaceId,
    commentsParams,
  });

  if (totalReplies === 0) {
    return null;
  }

  if (!replies || replies.length === 0) {
    return null;
  }

  const sortedReplies = replies.toSorted((a, b) => {
    return new Date(a.attributes.createdAt).getTime() - new Date(b.attributes.createdAt).getTime();
  });

  const initialReplies = sortedReplies.slice(0, INITIAL_REPLIES_TO_SHOW);
  const extraReplies = sortedReplies.slice(INITIAL_REPLIES_TO_SHOW);
  const hasExtraReplies = extraReplies.length > 0;

  return (
    <Stack
      spacing={3}
      sx={(theme) => ({
        px: 7,
        py: 4,
        borderTop: `1px solid ${theme.palette.divider}`,
      })}
    >
      {initialReplies.map((reply) => (
        <InlineReplyCard
          key={reply.id}
          reply={reply}
          workplaceId={workplaceId}
          viewerIsAuthor={isViewerAuthor({ workerInfo: reply.attributes.workerInfo, viewerUserId })}
          onLikeChanged={(updatedComment) => {
            updateComment(updatedComment);
          }}
          onRemoved={(commentId) => {
            deleteComment(commentId);
          }}
        />
      ))}

      {hasExtraReplies && (
        <Collapse unmountOnExit in={isExpanded} timeout={300}>
          <Stack spacing={3}>
            {extraReplies.map((reply) => (
              <InlineReplyCard
                key={reply.id}
                reply={reply}
                workplaceId={workplaceId}
                viewerIsAuthor={isViewerAuthor({
                  workerInfo: reply.attributes.workerInfo,
                  viewerUserId,
                })}
                onLikeChanged={(updatedComment) => {
                  updateComment(updatedComment);
                }}
                onRemoved={(commentId) => {
                  deleteComment(commentId);
                }}
              />
            ))}
          </Stack>
        </Collapse>
      )}

      {hasExtraReplies && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={(event: MouseEvent) => {
            event.stopPropagation();
            setIsExpanded((previous) => {
              const newIsExpanded = !previous;
              logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_INLINE_REPLIES_TOGGLED, {
                workplaceId,
                parentCommentId: parentComment.id,
                action: newIsExpanded ? "view" : "hide",
                totalReplies,
                workerId: viewerUserId,
              });
              return newIsExpanded;
            });
          }}
        >
          <CbhIcon type={isExpanded ? "chevron-up" : "chevron-down"} size="xSmall" />
          <Text semibold variant="body2">
            {isExpanded
              ? "Hide replies"
              : `View ${extraReplies.length} more ${pluralize("reply", extraReplies.length)}`}
          </Text>
        </Stack>
      )}
    </Stack>
  );
}
